/*
 * Chignole et Chignon — Custom behaviour layer
 *
 * Loaded deferred. No deps. Progressive enhancement.
 *
 * Modules
 *  stampBody       — body classes (template, ch-js-ready)
 *  tagRevealItems  — add .ch-stagger-item on outermost cards
 *  initReveal      — IO fallback (modern browsers use CSS scroll-timeline)
 *  initParallax    — hero image follows scroll (shared with scroll listener)
 *  initAccordion   — WAAPI height animation on <accordion-custom>
 *  initCartShake   — shake cart icon after add-to-cart
 *  initPricePulse  — bounce price on variant change
 *  initBlurUp      — blur-to-sharp images on load (PDP + cards only)
 *  initStickyATC   — sticky Add-to-Cart bar, mobile PDP
 *  initScrollTop   — floating scroll-to-top button
 *
 * A single scroll listener drives parallax + scroll-to-top visibility.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasScrollTimeline = (function () {
    try { return CSS.supports && CSS.supports('animation-timeline: view()'); }
    catch (e) { return false; }
  })();

  // Shared scroll subscriber: listeners push callbacks, one rAF drains them.
  var scrollListeners = [];
  var scrollTicking = false;
  function onScroll(cb) { scrollListeners.push(cb); }
  window.addEventListener('scroll', function () {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(function () {
      for (var i = 0; i < scrollListeners.length; i++) scrollListeners[i]();
      scrollTicking = false;
    });
  }, { passive: true });

  // -- stampBody ------------------------------------------------------
  function stampBody() {
    var main = document.getElementById('MainContent');
    var tpl = main && main.dataset.template;
    if (tpl) {
      document.body.classList.add('template-' + tpl.replace(/\./g, '-'));
      document.body.dataset.template = tpl;
    }
    document.body.classList.add('ch-js-ready');
  }

  // -- tagRevealItems -------------------------------------------------
  function tagRevealItems() {
    // Homepage: every section after the hero gets the fade-up reveal
    if (document.body.dataset.template === 'index') {
      var sections = document.querySelectorAll('main > .shopify-section');
      for (var i = 1; i < sections.length; i++) sections[i].classList.add('ch-reveal');
    }

    // Stagger reveal: outermost cards only
    document.querySelectorAll('main > .shopify-section').forEach(function (sec) {
      var cards = sec.querySelectorAll('product-card, .collection-card, [class*="collection-card"]');
      if (!cards.length) cards = sec.querySelectorAll('.card-gallery');
      var idx = 0;
      cards.forEach(function (card) {
        var parent = card.parentElement && card.parentElement.closest('.ch-stagger-item');
        if (parent) return; // already inside a tagged card
        card.classList.add('ch-stagger-item');
        card.style.setProperty('--ch-stagger-i', String(idx++));
      });
    });
  }

  // -- initReveal (IO fallback only — CSS handles modern browsers) ----
  function initReveal() {
    if (hasScrollTimeline || reducedMotion || !('IntersectionObserver' in window)) {
      // Either CSS handles it, or we're falling back to "everything visible"
      if (reducedMotion) {
        document.querySelectorAll('.ch-reveal').forEach(function (el) { el.classList.add('ch-reveal--in'); });
        document.querySelectorAll('.ch-stagger-item').forEach(function (el) { el.classList.add('ch-stagger-item--in'); });
      }
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(
          entry.target.classList.contains('ch-reveal') ? 'ch-reveal--in' : 'ch-stagger-item--in'
        );
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -80px 0px', threshold: 0.15 });
    document.querySelectorAll('.ch-reveal, .ch-stagger-item').forEach(function (el) { io.observe(el); });
  }

  // -- initParallax ---------------------------------------------------
  function initParallax() {
    if (reducedMotion) return;
    var hero = document.querySelector('.hero-wrapper');
    if (!hero) return;
    onScroll(function () {
      if (document.body.classList.contains('ch-scrolling-to-top')) return;
      var rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      var offset = Math.max(-120, Math.min(120, rect.top * -0.18));
      hero.style.setProperty('--ch-parallax', offset + 'px');
    });
  }

  // -- initAccordion --------------------------------------------------
  function initAccordion() {
    if (reducedMotion) return;
    document.querySelectorAll('accordion-custom').forEach(function (acc) {
      var details = acc.querySelector('details');
      if (!details) return;
      var summary = details.querySelector('summary');
      if (!summary) return;
      var children = Array.from(details.children).filter(function (c) { return c !== summary; });
      if (!children.length) return;

      var panel = details.querySelector('.ch-accordion-panel');
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'ch-accordion-panel';
        panel.style.overflow = 'hidden';
        children.forEach(function (c) { panel.appendChild(c); });
        details.appendChild(panel);
      }

      var animating = false;
      var ease = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
      summary.addEventListener('click', function (e) {
        if (acc.dataset.disableOnMobile === 'true' && matchMedia('(max-width: 749px)').matches) return;
        if (acc.dataset.disableOnDesktop === 'true' && matchMedia('(min-width: 750px)').matches) return;
        if (animating) { e.preventDefault(); return; }
        e.preventDefault();
        animating = true;

        var isOpen = details.open;
        if (!isOpen) {
          details.open = true;
          var h = panel.scrollHeight;
          panel.animate(
            [{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }],
            { duration: 280, easing: ease }
          ).onfinish = function () {
            panel.style.height = '';
            animating = false;
          };
        } else {
          var from = panel.scrollHeight;
          panel.animate(
            [{ height: from + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: 220, easing: ease }
          ).onfinish = function () {
            details.open = false;
            panel.style.height = '';
            animating = false;
          };
        }
      });
    });
  }

  // -- initCartShake --------------------------------------------------
  function initCartShake() {
    if (reducedMotion) return;
    function shake() {
      var icon = document.querySelector('.header__icon--cart, a[href="/cart"], a[href*="/cart"]');
      if (!icon) return;
      icon.classList.remove('ch-cart-shake');
      void icon.offsetWidth; // restart animation
      icon.classList.add('ch-cart-shake');
      setTimeout(function () { icon.classList.remove('ch-cart-shake'); }, 800);
    }
    // Theme CustomEvents (if fired)
    ['cart:refresh', 'cart:updated', 'cart-update', 'variant:add-to-cart']
      .forEach(function (name) { document.addEventListener(name, shake); });
    // Product-form submit fallback
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.action && /\/cart\/add/.test(e.target.action)) {
        setTimeout(shake, 400);
      }
    });
  }

  // -- initPricePulse -------------------------------------------------
  function initPricePulse() {
    if (reducedMotion) return;
    document.addEventListener('variant:change', function () {
      var price = document.querySelector('.product-price-container, .price, [class*="price--main"]');
      if (!price) return;
      price.classList.remove('ch-price-pulse');
      void price.offsetWidth;
      price.classList.add('ch-price-pulse');
      setTimeout(function () { price.classList.remove('ch-price-pulse'); }, 500);
    });
  }

  // -- initBlurUp -----------------------------------------------------
  function initBlurUp() {
    if (reducedMotion) return;
    var scopes = document.querySelectorAll('.product-media-container, media-gallery, .card-gallery');
    if (!scopes.length) return;

    function setup(img) {
      if (img.hasAttribute('data-ch-blur-done')) return;
      img.setAttribute('data-ch-blur', '');
      img.setAttribute('data-ch-blur-done', '');
      if (img.complete && img.naturalHeight > 0) {
        img.classList.add('ch-img-loaded');
      } else {
        img.addEventListener('load',  function () { img.classList.add('ch-img-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('ch-img-loaded'); }, { once: true });
      }
    }

    scopes.forEach(function (scope) { scope.querySelectorAll('img').forEach(setup); });

    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (records) {
      records.forEach(function (rec) {
        rec.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'IMG') setup(n);
          else if (n.querySelectorAll) n.querySelectorAll('img').forEach(setup);
        });
      });
    });
    scopes.forEach(function (s) { mo.observe(s, { childList: true, subtree: true }); });
  }

  // -- initStickyATC --------------------------------------------------
  function initStickyATC() {
    if (reducedMotion) return;
    var tpl = document.body.dataset.template || '';
    if (tpl.indexOf('product') !== 0) return;
    if (matchMedia('(min-width: 750px)').matches) return;

    var primary = document.querySelector(
      'form[action*="/cart/add"] button[type="submit"], product-form button[type="submit"], .product-form__submit, [name="add"]'
    );
    if (!primary) return;

    var title = document.querySelector('.product-title, h1.product__title, main h1');
    var priceSel = '.product-price-container, .price, [class*="price__amount"]';

    var bar = document.createElement('div');
    bar.className = 'ch-sticky-atc';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML =
      '<div class="ch-sticky-atc__info">' +
        '<span class="ch-sticky-atc__title"></span>' +
        '<span class="ch-sticky-atc__price"></span>' +
      '</div>' +
      '<button type="button" class="ch-sticky-atc__btn">Ajouter</button>';
    document.body.appendChild(bar);

    var titleEl = bar.querySelector('.ch-sticky-atc__title');
    var priceEl = bar.querySelector('.ch-sticky-atc__price');
    titleEl.textContent = title ? title.textContent.trim() : '';
    function refreshPrice() {
      var p = document.querySelector(priceSel);
      if (p) priceEl.textContent = p.textContent.trim().split('\n')[0];
    }
    refreshPrice();
    document.addEventListener('variant:change', refreshPrice);

    bar.querySelector('.ch-sticky-atc__btn').addEventListener('click', function () {
      primary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(function () { try { primary.click(); } catch (e) {} }, 520);
    });

    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('ch-sticky-atc--visible', !entry.isIntersecting);
        bar.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
      });
    }, { threshold: 0 }).observe(primary);
  }


  // -- initScrollTop --------------------------------------------------
  function initScrollTop() {
    if (document.querySelector('.ch-scroll-top')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ch-scroll-top';
    btn.setAttribute('aria-label', 'Retour en haut');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M6 15l6-6 6 6"/>' +
      '</svg>';
    document.body.appendChild(btn);

    var visible = false;
    onScroll(function () {
      var should = window.scrollY > 600;
      if (should !== visible) {
        visible = should;
        btn.classList.toggle('ch-scroll-top--visible', visible);
      }
    });

    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    btn.addEventListener('click', function () {
      var start = window.scrollY;
      if (start === 0) return;
      if (reducedMotion) { window.scrollTo(0, 0); return; }

      document.body.classList.add('ch-scrolling-to-top');

      // Custom rAF so we can: adapt duration to distance, use a smooth easing,
      // and avoid the fixed ~500ms that Chrome uses for window.scrollTo({behavior:'smooth'}).
      var duration = Math.min(1200, Math.max(650, start * 0.5));
      var t0 = performance.now();
      function step(now) {
        var p = Math.min(1, (now - t0) / duration);
        window.scrollTo(0, Math.round(start * (1 - easeInOutCubic(p))));
        if (p < 1) {
          requestAnimationFrame(step);
        } else {
          document.body.classList.remove('ch-scrolling-to-top');
        }
      }
      requestAnimationFrame(step);
    });
  }

  // -- boot -----------------------------------------------------------
  function boot() {
    stampBody();
    tagRevealItems();
    initReveal();
    initParallax();
    initAccordion();
    initCartShake();
    initPricePulse();
    initBlurUp();
    initStickyATC();
    initScrollTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
